import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
    CpmBadge,
    CpmButton,
    CpmCheckbox,
    CpmFilterDropdown,
} from '../../../components/design-system';
import { listStates, listStoreBrands } from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type { PlaceLoadState, StoreBrandListRow } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { formatLastToggleLabel, plural, stateName } from '../helpers';
import AppliedFilterChips, { type AppliedChip } from '../components/AppliedFilterChips';
import FilteredEmpty from '../components/FilteredEmpty';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import InfoI from '../components/InfoI';
import PencilButton from '../components/PencilButton';
import { ReportsAlert, ReportsSkeletonList, ReportsWait } from '../components/ReportsDialog';
import SearchField from '../components/SearchField';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import ApplyTemplateModal from '../Templates/ApplyTemplateModal';
import StoreBrandReportsModal from './StoreBrandReportsModal';

const COLS = '30px minmax(0, 1.3fr) minmax(220px, 1fr) 118px 168px 44px';

const HEAD: CSSProperties = {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'var(--cpm-text-primary)',
};

export default function BrandsBody() {
    const [search, setSearch] = useState('');
    const debounced = useDebouncedValue(search, 300);
    const [states, setStates] = useState<string[]>([]);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [statesError, setStatesError] = useState(false);
    const [rows, setRows] = useState<StoreBrandListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [place, setPlace] = useState<PlaceLoadState>('ready');
    const [listBusy, setListBusy] = useState(false);
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [excluded, setExcluded] = useState<string[]>([]);
    const [openId, setOpenId] = useState<string | null>(null);
    const [applyOpen, setApplyOpen] = useState(false);
    const [applyTargets, setApplyTargets] = useState<
        { storeBrandId: string; name?: string; state?: string | null }[]
    >([]);
    const [acting, setActing] = useState('');

    const query = debounced.trim();
    const searched = query.length >= 2;

    const loadStates = useCallback(async () => {
        setStatesError(false);
        try {
            setStateDocs(await listStates());
        } catch {
            setStatesError(true);
        }
    }, []);

    const load = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!searched) {
                setRows([]);
                setTotal(0);
                setPage(1);
                setListBusy(false);
                setMoreBusy(false);
                setMoreFailed(false);
                setPlace('ready');
                return;
            }
            if (!append) {
                setListBusy(true);
                setMoreFailed(false);
            } else {
                setMoreBusy(true);
                setMoreFailed(false);
            }
            try {
                const data = await listStoreBrands({
                    search: query,
                    states: states.length ? states : undefined,
                    page: nextPage,
                    limit: 25,
                });
                setRows((prev) => (append ? [...prev, ...data.storeBrands] : data.storeBrands));
                setTotal(data.total);
                setPage(nextPage);
                setPlace('ready');
            } catch {
                if (!append) setPlace('error');
                else setMoreFailed(true);
            } finally {
                setListBusy(false);
                setMoreBusy(false);
            }
        },
        [query, searched, states]
    );

    useEffect(() => {
        void loadStates();
    }, [loadStates]);

    useEffect(() => {
        setSelected([]);
        setSelectAllMatching(false);
        setExcluded([]);
        void load(1, false);
    }, [load]);

    const chips: AppliedChip[] = [];
    states.forEach((state) => {
        chips.push({
            key: `st-${state}`,
            label: state,
            onClear: () => setStates((prev) => prev.filter((item) => item !== state)),
        });
    });
    if (search.trim()) {
        chips.push({
            key: 'search',
            label: `Search: ${search.trim()}`,
            onClear: () => setSearch(''),
        });
    }

    const clearFilters = () => {
        setSearch('');
        setStates([]);
    };

    const selectedCount = selectAllMatching
        ? Math.max(total - excluded.length, 0)
        : selected.length;

    const isSelected = (id: string) =>
        selectAllMatching ? !excluded.includes(id) : selected.includes(id);

    const toggleRow = (id: string) => {
        if (selectAllMatching) {
            setExcluded((prev) =>
                prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
            );
            return;
        }
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const countText = useMemo(() => {
        const match =
            total === 1
                ? `1 store brand matches “${query}”`
                : `${total} store brands match “${query}”`;
        const stateNote = states.length ? ' with the state filter on' : '';
        const selectedNote = selectedCount ? `${selectedCount} selected · ` : '';
        return `${selectedNote}${match}${stateNote} · showing ${rows.length}`;
    }, [query, rows.length, selectedCount, states.length, total]);

    const applyLabel =
        selectedCount > 0
            ? `Apply a template to ${plural(selectedCount, 'store brand')}`
            : REPORTS_COPY.APPLY_TEMPLATE_TO_SELECTED;

    const filteredEmpty = searched && place === 'ready' && !listBusy && total === 0;

    const collectApplyTargets = async () => {
        if (!selectAllMatching) {
            return rows
                .filter((row) => selected.includes(row.storeBrandId))
                .map((row) => ({
                    storeBrandId: row.storeBrandId,
                    name: row.name,
                    state: row.state,
                }));
        }
        let collected = rows;
        let nextPage = page + 1;
        let knownTotal = total;
        while (collected.length < knownTotal) {
            const data = await listStoreBrands({
                search: query,
                states: states.length ? states : undefined,
                page: nextPage,
                limit: 25,
            });
            collected = [...collected, ...data.storeBrands];
            knownTotal = data.total;
            if (data.storeBrands.length === 0) break;
            nextPage += 1;
        }
        return collected
            .filter((row) => !excluded.includes(row.storeBrandId))
            .map((row) => ({
                storeBrandId: row.storeBrandId,
                name: row.name,
                state: row.state,
            }));
    };

    const openApply = async () => {
        if (selectedCount === 0 || acting) return;
        setActing('Collecting the store brands you picked…');
        try {
            setApplyTargets(await collectApplyTargets());
            setApplyOpen(true);
        } finally {
            setActing('');
        }
    };

    const busy = listBusy || Boolean(acting);

    return (
        <div style={{ padding: '16px 20px 22px' }}>
            <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder={REPORTS_COPY.SEARCH_STORE_BRANDS}
                    size="sm"
                />
                <CpmFilterDropdown
                    label="State"
                    triggerVariant="chip"
                    portal
                    menuMinWidth={286}
                    failed={statesError}
                    failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                    onRetry={() => void loadStates()}
                    options={stateDocs
                        .map(stateName)
                        .filter(Boolean)
                        .map((name) => ({ value: name, label: name }))}
                    selected={states}
                    onChange={setStates}
                />
                <div className="flex items-center" style={{ marginLeft: 'auto' }}>
                    <InfoI
                        title={REPORTS_TIPS.STORE_BRAND.title}
                        body={REPORTS_TIPS.STORE_BRAND.body}
                    />
                </div>
            </div>
            <AppliedFilterChips chips={chips} onClearAll={clearFilters} />

            {!searched ? (
                <EmptyUntilSearch
                    note={
                        search.trim().length === 1
                            ? REPORTS_COPY.EMPTY_BRANDS_ONE_CHAR
                            : REPORTS_COPY.EMPTY_BRANDS_BODY
                    }
                />
            ) : (
                <div>
                    <div
                        className="flex items-center justify-between flex-wrap"
                        style={{ gap: 14, margin: '16px 0 10px' }}
                    >
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {countText}
                        </div>
                        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                            <CpmButton
                                variant="outline"
                                size="sm"
                                disabled={busy || total === 0}
                                onClick={() => {
                                    setSelectAllMatching(true);
                                    setExcluded([]);
                                    setSelected([]);
                                }}
                            >
                                {REPORTS_COPY.SELECT_ALL_MATCHING}
                            </CpmButton>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={busy || selectedCount === 0}
                                onClick={() => {
                                    setSelected([]);
                                    setSelectAllMatching(false);
                                    setExcluded([]);
                                }}
                            >
                                {REPORTS_COPY.CLEAR_SELECTION}
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="sm"
                                disabled={busy || selectedCount === 0}
                                onClick={() => void openApply()}
                            >
                                {applyLabel}
                            </CpmButton>
                        </div>
                    </div>

                    {acting ? (
                        <div style={{ paddingBottom: 12 }}>
                            <ReportsWait label={acting} />
                        </div>
                    ) : null}

                    {listBusy ? (
                        <div>
                            <div style={{ paddingBottom: 12 }}>
                                <ReportsWait label={REPORTS_COPY.LOADING_BRANDS} />
                            </div>
                            <ReportsSkeletonList rows={3} />
                        </div>
                    ) : null}

                    {!listBusy && place === 'error' ? (
                        <ReportsAlert
                            tone="danger"
                            title={REPORTS_COPY.ERROR_BRANDS_TITLE}
                            body={REPORTS_COPY.ERROR_BRANDS_BODY}
                            action={
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void load(1, false)}
                                >
                                    {REPORTS_COPY.RETRY_BRANDS}
                                </CpmButton>
                            }
                        />
                    ) : null}

                    {!listBusy && filteredEmpty ? (
                        <FilteredEmpty
                            title={REPORTS_COPY.FILTERED_EMPTY_BRANDS}
                            cause={REPORTS_COPY.FILTERED_EMPTY_BRANDS_CAUSE}
                            restated={
                                chips.length
                                    ? `Still on: ${chips.map((chip) => chip.label).join(' · ')}.`
                                    : undefined
                            }
                            onClear={clearFilters}
                        />
                    ) : null}

                    {!listBusy && place === 'ready' && rows.length > 0 ? (
                        <div
                            className="overflow-auto"
                            style={{
                                maxHeight: 580,
                                border: '1px solid var(--cpm-border-default)',
                                borderRadius: 10,
                            }}
                        >
                            <div
                                className="sticky top-0 z-[3] grid items-center"
                                style={{
                                    gridTemplateColumns: COLS,
                                    gap: 12,
                                    padding: '10px 16px',
                                    background: 'var(--cpm-purple-tint-13)',
                                    borderBottom: '1.5px solid var(--cpm-border-strong)',
                                }}
                            >
                                <span />
                                <span style={HEAD}>Store brand</span>
                                <span style={HEAD}>State</span>
                                <span style={{ ...HEAD, textAlign: 'right' }}>Reports on</span>
                                <span style={HEAD}>Last toggle</span>
                                <span />
                            </div>
                            {rows.map((row) => {
                                const on = row.enabledCount ?? 0;
                                return (
                                    <div
                                        key={row.storeBrandId}
                                        className="grid items-center"
                                        onClick={() => setOpenId(row.storeBrandId)}
                                        style={{
                                            gridTemplateColumns: COLS,
                                            gap: 12,
                                            padding: '11px 16px',
                                            borderTop: '1px solid var(--cpm-border-default)',
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                                'var(--cpm-purple-tint-3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <span
                                            className="inline-flex"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <CpmCheckbox
                                                checked={isSelected(row.storeBrandId)}
                                                onChange={() => toggleRow(row.storeBrandId)}
                                            />
                                        </span>
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                    color: 'var(--cpm-text-primary)',
                                                }}
                                            >
                                                {row.name}
                                            </div>
                                        </div>
                                        <div
                                            className="flex flex-wrap items-center"
                                            style={{ gap: 4 }}
                                        >
                                            {stateLabels(row).map((label) => (
                                                <CpmBadge key={label} tone="neutral">
                                                    {label}
                                                </CpmBadge>
                                            ))}
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-text-primary)',
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {on}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 1,
                                                    fontSize: 10.5,
                                                    fontWeight: 700,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {on === 0
                                                    ? REPORTS_COPY.NONE_ON
                                                    : on === 1
                                                      ? REPORTS_COPY.REPORT_ON
                                                      : REPORTS_COPY.REPORTS_ON_NOTE}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            {formatLastToggleLabel(row.lastToggledAt)}
                                        </div>
                                        <div className="flex justify-end">
                                            <PencilButton
                                                label={REPORTS_COPY.OPEN_STORE_BRAND}
                                                onClick={() => setOpenId(row.storeBrandId)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            <InfiniteScrollSentinel
                                hasMore={rows.length < total}
                                loading={moreBusy}
                                error={moreFailed}
                                onLoadMore={() => void load(page + 1, true)}
                                showEnd={rows.length > 0}
                                endVariant="rule"
                            />
                        </div>
                    ) : null}
                </div>
            )}

            {openId ? (
                <StoreBrandReportsModal
                    storeBrandID={openId}
                    onClose={() => setOpenId(null)}
                    onSaved={() => void load(1, false)}
                />
            ) : null}
            {applyOpen ? (
                <ApplyTemplateModal
                    source="brands"
                    presetStoreBrands={applyTargets}
                    onClose={() => setApplyOpen(false)}
                    onApplied={() => {
                        setApplyOpen(false);
                        setSelected([]);
                        setSelectAllMatching(false);
                        setExcluded([]);
                        void load(1, false);
                    }}
                />
            ) : null}
        </div>
    );
}

function stateLabels(row: StoreBrandListRow): string[] {
    if (row.state) return [row.state];
    if (row.mixedStates?.length) return row.mixedStates;
    return ['—'];
}

function EmptyUntilSearch({ note }: { note: string }) {
    return (
        <div
            className="flex items-start"
            style={{
                marginTop: 14,
                gap: 14,
                padding: 20,
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 10,
                background: 'var(--cpm-surface-subtle)',
            }}
        >
            <span
                className="inline-flex items-center justify-center shrink-0"
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: '#fff',
                    border: '1px solid var(--cpm-border-strong)',
                    color: 'var(--cpm-text-secondary)',
                }}
            >
                <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M3 21V8l9-5 9 5v13" />
                    <path d="M9 21v-8h6v8" />
                </svg>
            </span>
            <div className="min-w-0 flex-1">
                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 800,
                        letterSpacing: '-0.01em',
                        color: 'var(--cpm-text-primary)',
                    }}
                >
                    {REPORTS_COPY.EMPTY_BRANDS_TITLE}
                </div>
                <div
                    style={{
                        marginTop: 4,
                        fontSize: 12.5,
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {note}
                </div>
            </div>
        </div>
    );
}
